// TaskFlow · filas de tarea reutilizables: casilla accesible, metadatos con estado (ícono + palabra),
// completar con «Deshacer», reordenar (arrastrar, Alt+↑/↓ o menú «Mover a…»), deslizar para completar.

import { api, estado } from './api.js';
import { e, icono, fechaRelativa, hora12, hoyISO, diasEntre, aviso, anunciar, menu, plural, uuid } from './util.js';

/** Etiquetas de estado de una tarea. */
export function estadosTarea(t) {
  const hoy = hoyISO();
  const s = [];
  if (t.completada_en) s.push(`<span class="tf-estado tf-estado--completada">${icono('check')}Completada</span>`);
  else if (t.fecha && t.fecha < hoy) {
    const d = diasEntre(t.fecha, hoy);
    s.push(`<span class="tf-estado tf-estado--vencida">${icono('alerta')}Vencida ${d === 1 ? 'ayer' : `hace ${d} días`}</span>`);
  } else if (t.fecha === hoy) s.push(`<span class="tf-estado tf-estado--hoy">${icono('sol')}Hoy</span>`);
  else if (t.fecha) s.push(`<span>${e(fechaRelativa(t.fecha))}</span>`);
  if (t.hora && t.fecha) s.push(`<span class="tf-hora">${e(hora12(t.hora))}</span>`);
  if (t.prioridad === 3) s.push(`<span class="tf-estado tf-estado--alta">${icono('bandera')}Alta</span>`);
  else if (t.prioridad === 2) s.push('<span>Prioridad media</span>');
  if (t.programada) s.push(`<span class="tf-estado tf-estado--programada">${icono('reloj')}Visible ${e(fechaRelativa(t.visible_desde.slice(0, 10)).toLowerCase())}</span>`);
  if (t.recurrencia_texto) s.push(`<span class="tf-estado tf-estado--recurrente">${icono('repetir')}${e(t.recurrencia_texto)}</span>`);
  if (t.reabierta) s.push(`<span class="tf-estado tf-estado--reabierta">${icono('reabrir')}Reabierta</span>`);
  if (t.avance) s.push(`<span class="tf-estado">${icono('personas')}${t.avance.hechas} de ${t.avance.total}</span>`);
  if (!t.fecha && t.foco) s.push(`<span class="tf-estado tf-estado--programada">${icono('foco')}Foco</span>`);
  if (t.avisar_whatsapp) s.push(`<span class="tf-estado" title="Avisa por WhatsApp 10 minutos antes">${icono('whatsapp')}<span class="solo-lector">Aviso por </span>WhatsApp</span>`);
  if (t.adjuntos) s.push(`<span>${icono('clip', 'icono') .replace('class="icono"', 'class="icono" style="width:14px;height:14px"')}${t.adjuntos}</span>`);
  (t.etiquetas || []).forEach((x) => s.push(`<span>#${e(x)}</span>`));
  return s.join('');
}

/**
 * HTML de una fila. opciones: {mostrarPila, ordenable, seleccionada}
 */
export function filaTarea(t, op = {}) {
  const hecha = !!t.completada_en;
  const vencida = !hecha && t.fecha && t.fecha < hoyISO();
  const clases = ['tf-tarea', vencida ? 'tf-tarea--vencida' : '', hecha ? 'tf-tarea--hecha' : '', op.seleccionada ? 'tf-tarea--seleccionada' : '', t._pendiente ? 'pendiente-sync' : ''].join(' ');
  const accion = hecha ? 'Reabrir' : 'Completar';
  const casilla = t.es_grupo
    ? `<span class="tf-casilla" aria-hidden="true" title="Vista de seguimiento">${icono('personas')}</span>`
    : `<button type="button" class="tf-casilla" role="checkbox" aria-checked="${hecha}" aria-label="${accion}: ${e(t.titulo)}" data-accion="alternar" ${t._soloLectura ? 'aria-disabled="true"' : ''}>${icono('check')}</button>`;
  const asignado = t.asignado && t.asignado.uid !== estado.usuario?.uid
    ? `<span class="tf-avatar" title="Asignada a ${e(t.asignado.nombre)}"><span aria-hidden="true">${e(t.asignado.iniciales)}</span><span class="solo-lector">Asignada a ${e(t.asignado.nombre)}</span></span>` : '';
  return `<div role="listitem" class="${clases}" data-uid="${e(t.uid)}" ${op.ordenable ? 'draggable="true"' : ''}>
    ${casilla}
    <div class="tf-tarea__cuerpo">
      <div class="tf-tarea__titulo"><a href="/tarea/${e(t.uid)}" data-enlace>${e(t.titulo)}</a></div>
      <div class="tf-tarea__meta">${estadosTarea(t)}${op.mostrarPila ? `<span>${e(t.pila.nombre)} › ${e(t.lista.nombre)}</span>` : ''}</div>
    </div>
    <div class="tf-tarea__acciones">${asignado}
      ${op.ordenable ? `<button type="button" class="boton-icono asa" data-accion="opciones" aria-haspopup="menu" aria-expanded="false" aria-label="Opciones de ${e(t.titulo)}: mover, subir o bajar (Alt+↑ y Alt+↓)">${icono('asa')}</button>`
        : `<button type="button" class="boton-icono" data-accion="opciones" aria-haspopup="menu" aria-expanded="false" aria-label="Opciones de ${e(t.titulo)}">${icono('mas_opciones')}</button>`}
    </div></div>`;
}

export function listaTareas(tareas, op = {}) {
  return `<div class="tarjeta-lista" role="list" aria-label="${e(op.etiqueta || 'Tareas')}">${tareas.map((t) => filaTarea(t, op)).join('')}${op.extra || ''}</div>`;
}

/** Completa o reabre con actualización optimista y aviso «Deshacer». */
export async function alternarTarea(t, fila, alTerminar) {
  const completar = !t.completada_en;
  const ruta = `/api/tareas/${t.uid}/${completar ? 'completar' : 'reabrir'}`;
  t.completada_en = completar ? new Date().toISOString() : null;
  if (fila) {
    fila.classList.toggle('tf-tarea--hecha', completar);
    const c = fila.querySelector('.tf-casilla');
    c?.setAttribute('aria-checked', String(completar));
    c?.setAttribute('aria-label', `${completar ? 'Reabrir' : 'Completar'}: ${t.titulo}`);
  }
  try {
    const r = await api.enviar(ruta, {}, `${completar ? 'Completar' : 'Reabrir'} «${t.titulo}»`);
    const texto = completar ? 'Tarea completada' : 'Tarea reabierta';
    anunciar(`${texto}: ${t.titulo}`);
    aviso(r.encolada ? `${texto} · se enviará al reconectar` : texto, {
      deshacer: async () => {
        await api.enviar(`/api/tareas/${t.uid}/${completar ? 'reabrir' : 'completar'}`, {}, 'Deshacer');
        // Si al completar se creó la siguiente repetición, se elimina para no duplicarla.
        if (completar && r.siguiente) await api.post(`/api/tareas/${r.siguiente}/eliminar`).catch(() => {});
        alTerminar?.();
      },
    });
    if (r.siguiente) anunciar('Se creó la siguiente repetición');
    setTimeout(() => alTerminar?.(), r.encolada ? 0 : 4000);
  } catch (err) {
    t.completada_en = completar ? null : new Date().toISOString();
    if (fila) fila.classList.toggle('tf-tarea--hecha', !completar);
    aviso(err.message);
  }
}

/**
 * Conecta los eventos de un contenedor con filas de tarea.
 * ctx: {tareas, alAbrir(uid), recargar(), ordenable, listaUid, listas}
 */
export function conectarFilas(raiz, ctx) {
  const buscar = (uid) => ctx.tareas.find((t) => t.uid === uid);
  raiz.addEventListener('click', (ev) => {
    const fila = ev.target.closest('.tf-tarea');
    if (!fila) return;
    const t = buscar(fila.dataset.uid);
    if (!t) return;
    const b = ev.target.closest('[data-accion]');
    if (b?.dataset.accion === 'alternar') {
      if (b.getAttribute('aria-disabled') === 'true') { aviso('Tienes acceso de lector a esta pila: no puedes completar tareas.'); return; }
      alternarTarea(t, fila, ctx.recargar);
    } else if (b?.dataset.accion === 'opciones') {
      opcionesTarea(b, t, fila, ctx);
    } else if (ev.target.closest('[data-enlace]')) {
      ev.preventDefault();
      ctx.alAbrir(t.uid);
    }
  });
  // Teclado: Alt+↑/↓ mueve la tarea; Enter sobre la fila abre.
  raiz.addEventListener('keydown', (ev) => {
    const fila = ev.target.closest('.tf-tarea');
    if (!fila || !ctx.ordenable || !ev.altKey || (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown')) return;
    ev.preventDefault();
    moverFila(fila, ev.key === 'ArrowUp' ? -1 : 1, ctx);
  });
  if (ctx.ordenable) conectarArrastre(raiz, ctx);
  conectarDeslizar(raiz, ctx);
}

function opcionesTarea(boton, t, fila, ctx) {
  const lector = t._soloLectura;
  const opciones = [
    { texto: 'Abrir detalle', icono: 'editar', accion: () => ctx.alAbrir(t.uid) },
    { texto: t.completada_en ? 'Reabrir' : 'Completar', icono: t.completada_en ? 'reabrir' : 'check', deshabilitado: lector || t.es_grupo,
      ayuda: t.es_grupo ? 'Cada persona completa su propia copia.' : 'Tienes acceso de lector.', accion: () => alternarTarea(t, fila, ctx.recargar) },
  ];
  if (ctx.ordenable) {
    opciones.push('-',
      { texto: 'Subir', icono: 'arriba', accion: () => moverFila(fila, -1, ctx) },
      { texto: 'Bajar', icono: 'abajo', accion: () => moverFila(fila, 1, ctx) });
    if (ctx.listas && ctx.listas.length > 1) {
      opciones.push({ texto: 'Mover a otra lista…', icono: 'lista', deshabilitado: lector, ayuda: 'Tienes acceso de lector.', accion: () => moverAOtraLista(t, ctx) });
    }
  }
  if (!t.fecha && !t.completada_en && !t.padre_uid) {
    opciones.push({ texto: t.foco ? 'Quitar de foco' : 'Marcar como foco', icono: 'foco', deshabilitado: lector, ayuda: 'Tienes acceso de lector.', accion: async () => {
      t.foco = !t.foco; await api.enviar(`/api/tareas/${t.uid}/editar`, { foco: t.foco }, 'Foco'); aviso(t.foco ? 'Marcada como foco' : 'Ya no está en foco'); ctx.recargar(); } });
  }
  menu(boton, opciones);
}

function moverAOtraLista(t, ctx) {
  const otras = ctx.listas.filter((l) => l.uid !== ctx.listaUid);
  const b = document.querySelector(`.tf-tarea[data-uid="${t.uid}"] [data-accion="opciones"]`);
  menu(b, otras.map((l) => ({ texto: l.nombre, icono: 'lista', accion: async () => {
    try { await api.post(`/api/tareas/${t.uid}/mover`, { lista_uid: l.uid }); aviso(`Movida a ${l.nombre}`); ctx.recargar(); } catch (err) { aviso(err.message); }
  } })));
}

async function moverFila(fila, delta, ctx) {
  const hermanos = [...fila.parentElement.querySelectorAll('.tf-tarea:not(.tf-tarea--hecha)')];
  const i = hermanos.indexOf(fila);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= hermanos.length) { anunciar(delta < 0 ? 'Ya está de primera' : 'Ya está de última'); return; }
  if (delta < 0) hermanos[j].before(fila); else hermanos[j].after(fila);
  fila.querySelector('[data-accion="opciones"]')?.focus();
  anunciar(`Movida a la posición ${j + 1} de ${hermanos.length}`);
  await guardarOrden(fila.parentElement, ctx);
}

async function guardarOrden(contenedor, ctx) {
  const uids = [...contenedor.querySelectorAll('.tf-tarea')].map((f) => f.dataset.uid);
  try { await api.enviar(`/api/listas/${ctx.listaUid}/ordenar`, { uids }, 'Ordenar lista'); } catch (err) { aviso(err.message); }
}

function conectarArrastre(raiz, ctx) {
  let arrastrada = null;
  raiz.addEventListener('dragstart', (ev) => {
    arrastrada = ev.target.closest('.tf-tarea');
    if (!arrastrada) return;
    arrastrada.classList.add('arrastrando');
    ev.dataTransfer.effectAllowed = 'move';
    ev.dataTransfer.setData('text/plain', arrastrada.dataset.uid);
  });
  raiz.addEventListener('dragover', (ev) => {
    const sobre = ev.target.closest('.tf-tarea');
    if (!arrastrada || !sobre || sobre === arrastrada) return;
    ev.preventDefault();
    raiz.querySelectorAll('.sobre').forEach((x) => x.classList.remove('sobre'));
    sobre.classList.add('sobre');
  });
  raiz.addEventListener('drop', (ev) => {
    const sobre = ev.target.closest('.tf-tarea');
    if (!arrastrada || !sobre) return;
    ev.preventDefault();
    sobre.before(arrastrada);
    guardarOrden(sobre.parentElement, ctx);
  });
  raiz.addEventListener('dragend', () => {
    raiz.querySelectorAll('.sobre').forEach((x) => x.classList.remove('sobre'));
    arrastrada?.classList.remove('arrastrando');
    arrastrada = null;
  });
}

/** Gesto opcional en pantallas táctiles: deslizar a la derecha completa. La casilla sigue siempre visible. */
function conectarDeslizar(raiz, ctx) {
  let x0 = 0, y0 = 0, fila = null, dx = 0;
  raiz.addEventListener('touchstart', (ev) => {
    fila = ev.target.closest('.tf-tarea');
    if (!fila || ev.target.closest('button, a')) { fila = null; return; }
    x0 = ev.touches[0].clientX; y0 = ev.touches[0].clientY; dx = 0;
  }, { passive: true });
  raiz.addEventListener('touchmove', (ev) => {
    if (!fila) return;
    dx = ev.touches[0].clientX - x0;
    if (Math.abs(ev.touches[0].clientY - y0) > 30) { fila.style.transform = ''; fila = null; return; }
    if (dx > 0) { fila.style.transform = `translateX(${Math.min(dx, 120)}px)`; fila.classList.toggle('fondo-deslizar', dx > 90); }
  }, { passive: true });
  raiz.addEventListener('touchend', () => {
    if (!fila) return;
    const f = fila; fila = null;
    f.style.transform = ''; f.classList.remove('fondo-deslizar');
    if (dx > 90) {
      const t = ctx.tareas.find((x) => x.uid === f.dataset.uid);
      if (t && !t.es_grupo && !t._soloLectura) alternarTarea(t, f, ctx.recargar);
    }
  });
}

/** Creación rápida en línea: escribe y Enter. Funciona sin conexión. */
export function creacionRapida(listaUid, { marcador = 'Agregar tarea', extra = {} } = {}) {
  return `<form class="rapida" data-rapida="${e(listaUid)}" data-extra='${e(JSON.stringify(extra))}'>
    <span class="icono-mas" aria-hidden="true">${icono('mas')}</span>
    <label class="solo-lector" for="rapida-${e(listaUid)}">${e(marcador)}</label>
    <input id="rapida-${e(listaUid)}" autocomplete="off" placeholder="${e(marcador)} y presiona Enter" maxlength="300">
  </form>`;
}

export function conectarRapida(raiz, alCrear) {
  raiz.addEventListener('submit', async (ev) => {
    const f = ev.target.closest('form[data-rapida]');
    if (!f) return;
    ev.preventDefault();
    const input = f.querySelector('input');
    const titulo = input.value.trim();
    if (!titulo) return;
    const extra = JSON.parse(f.dataset.extra || '{}');
    const t = { uid: uuid(), titulo, lista_uid: f.dataset.rapida, ...extra };
    input.value = '';
    try {
      const r = await api.enviar('/api/tareas', t, `Crear «${titulo}»`);
      anunciar(r.encolada ? `Tarea creada en este dispositivo: ${titulo}` : `Tarea creada: ${titulo}`);
      alCrear?.(t, r);
    } catch (err) { input.value = titulo; aviso(err.message); }
  });
}

export function vacio(titulo, texto) { return `<div class="vacio"><strong>${e(titulo)}</strong>${e(texto)}</div>`; }
export function esqueletos(n = 4) { return `<div class="tf-pila" aria-hidden="true">${'<div class="esqueleto"></div>'.repeat(n)}</div>`; }
export { plural };
